/**
 * articleSlug.ts
 * ──────────────
 * Source de vérité pour les slugs des 35 codes de loi indexés dans Qdrant.
 * Utilisé partout où on construit un lien vers un article de loi.
 *
 * Slugs générés depuis les codes réels stockés dans Qdrant
 * (collection code_articles_qwen3, scan exhaustif).
 *
 * Synchronisé avec :
 *   - backend/routes/articles.py  (SLUG_TO_CODE)
 *   - scripts/enrich_articles_slugs.py  (CODE_TO_SLUG)
 */

// ── Mapping code canonique → slug ─────────────────────────────────────────────

export const CODE_TO_SLUG: Record<string, string> = {
    'Code civil':                                                         'code-civil',
    'Code de commerce':                                                   'code-de-commerce',
    'Code de justice administrative':                                     'code-de-justice-administrative',
    "Code de l'action sociale et des familles":                           'code-de-laction-sociale-et-des-familles',
    "Code de l'entrée et du séjour des étrangers et du droit d'asile":   'code-de-lentree-et-du-sejour-des-etrangers-et-du-droit-dasile',
    "Code de l'environnement":                                            'code-de-lenvironnement',
    "Code de l'expropriation pour cause d'utilité publique":              'code-de-lexpropriation-pour-cause-dutilite-publique',
    "Code de l'organisation judiciaire":                                  'code-de-lorganisation-judiciaire',
    "Code de l'urbanisme":                                                'code-de-lurbanisme',
    'Code de la commande publique':                                       'code-de-la-commande-publique',
    'Code de la consommation':                                            'code-de-la-consommation',
    "Code de la construction et de l'habitation":                         'code-de-la-construction-et-de-lhabitation',
    'Code de la justice pénale des mineurs':                              'code-de-la-justice-penale-des-mineurs',
    'Code de la propriété intellectuelle':                                'code-de-la-propriete-intellectuelle',
    'Code de la route':                                                   'code-de-la-route',
    'Code de la sécurité intérieure':                                     'code-de-la-securite-interieure',
    'Code de la sécurité sociale':                                        'code-de-la-securite-sociale',
    'Code de procédure civile':                                           'code-de-procedure-civile',
    'Code de procédure pénale':                                           'code-de-procedure-penale',
    'Code des assurances':                                                'code-des-assurances',
    'Code des communes':                                                  'code-des-communes',
    'Code des douanes':                                                   'code-des-douanes',
    'Code des postes et des communications électroniques':                'code-des-postes-et-des-communications-electroniques',
    "Code des procédures civiles d'exécution":                            'code-des-procedures-civiles-dexecution',
    "Code des relations entre le public et l'administration":             'code-des-relations-entre-le-public-et-ladministration',
    'Code du travail':                                                    'code-du-travail',
    'Code général de la fonction publique':                               'code-general-de-la-fonction-publique',
    'Code général de la propriété des personnes publiques':               'code-general-de-la-propriete-des-personnes-publiques',
    'Code général des collectivités territoriales':                       'code-general-des-collectivites-territoriales',
    'Code général des impôts annexe I, CGIANI':                           'code-general-des-impots-annexe-i-cgiani',
    'Code général des impôts, annexe II, CGIANII':                        'code-general-des-impots-annexe-ii-cgianii',
    'Code général des impôts, annexe III, CGIANIII':                      'code-general-des-impots-annexe-iii-cgianiii',
    'Code général des impôts, annexe IV, CGIANIV':                        'code-general-des-impots-annexe-iv-cgianiv',
    'Code monétaire et financier':                                        'code-monetaire-et-financier',
    'Code pénal':                                                         'code-penal',
    'Nouveau Code de procédure civile':                                   'code-de-procedure-civile',
    'nouveau code de procédure civile':                                   'code-de-procedure-civile',
    // ── Règlements européens et lois spéciales ─────────────────────────────
    'Règlement (UE) 2016/679 (RGPD)':                                    'reglement-ue-2016-679',
    'Règlement UE 2016/679':                                              'reglement-ue-2016-679',
    'Règlement général sur la protection des données':                    'reglement-ue-2016-679',
    'RGPD':                                                               'reglement-ue-2016-679',
    'GDPR':                                                               'reglement-ue-2016-679',
    'Loi n°78-17 du 6 janvier 1978 relative à l\'informatique, aux fichiers et aux libertés': 'loi-informatique-et-libertes',
    'Loi n°78-17':                                                        'loi-informatique-et-libertes',
    'Loi Informatique et Libertés':                                       'loi-informatique-et-libertes',
    'Loi informatique et libertés':                                       'loi-informatique-et-libertes',
    'Livre des procédures fiscales':                                      'livre-des-procedures-fiscales',
    'LPF':                                                                'livre-des-procedures-fiscales',
    // ── Sigles / noms usuels stockés tels quels par l'extraction LLM ───────
    'CESEDA':                                                             'code-de-lentree-et-du-sejour-des-etrangers-et-du-droit-dasile',
    'Règlement Dublin III':                                               'reglement-ue-2013-604',
    'Dublin III':                                                         'reglement-ue-2013-604',
    'Règlement (UE) n° 604/2013':                                         'reglement-ue-2013-604',
  }
  
  // Couvre aussi les variantes avec point final stockées dans certaines fiches
  // ex: "Code de la route." → "code-de-la-route"
  const CODE_TO_SLUG_EXTENDED: Record<string, string> = { ...CODE_TO_SLUG }
  for (const [code, slug] of Object.entries(CODE_TO_SLUG)) {
    CODE_TO_SLUG_EXTENDED[code + '.'] = slug
  }
  
  // ── Inverse : slug → code canonique ──────────────────────────────────────────
  
  export const SLUG_TO_CODE: Record<string, string> = Object.fromEntries(
    Object.entries(CODE_TO_SLUG).map(([code, slug]) => [slug, code])
  )
  
  // ── Helpers ───────────────────────────────────────────────────────────────────
  
  /**
   * Retourne le slug d'un code de loi.
   * Retourne null si le code est inconnu (ne devrait pas arriver avec les 35 codes).
   *
   * @example
   * slugifyCode('Code de commerce')     // → 'code-de-commerce'
   * slugifyCode('Code de la route.')    // → 'code-de-la-route'  (point final toléré)
   * slugifyCode('code inconnu')         // → null
   */
  export function slugifyCode(code: string): string | null {
    // 1. Correspondance exacte
    const slug = CODE_TO_SLUG_EXTENDED[code] ?? CODE_TO_SLUG_EXTENDED[code.trim()]
    if (slug) return slug
  
    // 2. Insensible à la casse + point final toléré
    const normalized = code.trim().toLowerCase().replace(/\.$/, '')
    for (const [key, val] of Object.entries(CODE_TO_SLUG_EXTENDED)) {
      if (key.toLowerCase().replace(/\.$/, '') === normalized) return val
    }

    // 3. Loi Informatique et Libertés : l'extraction LLM varie le libellé
    //    (« Loi n°78-17 du 6 janvier 1978 », « loi n° 78-17 », « LIL »…)
    if (/\b78\s*-\s*17\b|informatique.{0,20}libert|^lil$/i.test(normalized))
      return 'loi-informatique-et-libertes'

    return null
  }
  
  /** Règlements EU → /reglements/..., codes français → /articles/... */
  export function isEuRegSlug(slug: string): boolean {
    return slug.startsWith('reglement-ue-')
  }

  /**
   * Construit le chemin vers la page d'un article.
   * Règlements EU → /reglements/:slug/:numero
   * Codes français → /articles/:slug/:numero
   */
  export function articlePath(code: string, numero: string): string {
    const slug = slugifyCode(code)
    if (slug) {
      const base = isEuRegSlug(slug) ? '/reglements' : '/articles'
      return `${base}/${slug}/${encodeURIComponent(numero)}`
    }
    return `/articles/article-lookup?code=${encodeURIComponent(code)}&numero=${encodeURIComponent(numero)}`
  }
  
  /**
   * Construit le chemin depuis un objet article de fiche
   * (tel que stocké dans fiche.articles[]).
   *
   * Priorité :
   *   1. node_id Qdrant si disponible (lookup direct, zéro ambiguïté)
   *   2. slug_code + article (slug pré-calculé par enrich_articles_slugs.py)
   *   3. articlePath(code, article) (calcul à la volée)
   *
   * @example
   * articlePathFromFiche({ code: 'Code de commerce', article: 'L622-17', node_id: 'abc' })
   * // → '/articles/by-node-id/abc'
   *
   * articlePathFromFiche({ code: 'Code de commerce', article: 'L622-17', slug_code: 'code-de-commerce' })
   * // → '/articles/code-de-commerce/L622-17'
   */
  /**
   * Retourne le chemin vers la page d'un article, ou null si le code
   * n'est pas indexé et qu'il n'y a pas de node_id.
   */
  /**
   * Numéro d'article pour l'URL : "L. 740-1" → "L740-1", "24 § 3" → "24".
   * Le paragraphe/alinéa n'a pas de page dédiée côté API.
   */
  function urlArticleNumero(numero: string): string {
    return cleanArticleNumero(numero)
      .replace(/\s*(§|al\.|alinéa|paragraphe)\s*.*$/i, '')
      .replace(/^([LRDA])\.?\s*/i, '$1')
      .trim()
  }

  export function articlePathFromFiche(art: {
    code:       string
    article:    string
    node_id?:   string | null
    slug_code?: string | null
    /** Date de la version applicable (lois dont la numérotation a changé). */
    version_date?: string | null
  }): string | null {
    if (art.node_id) {
      return `/articles/by-node-id/${art.node_id}`
    }
    const num  = urlArticleNumero(art.article)
    const slug = art.slug_code || slugifyCode(art.code)
    if (!slug) return null
    const base  = isEuRegSlug(slug) ? '/reglements' : '/articles'
    const query = art.version_date ? `?date=${encodeURIComponent(art.version_date)}` : ''
    return `${base}/${slug}/${encodeURIComponent(num)}${query}`
  }
  
  /**
   * Libellé court pour afficher un article dans l'UI.
   *
   * @example
   * articleLabel('Code de commerce', 'L622-17') // → 'C. com. art. L622-17'
   * articleLabel('Code civil', '1240')           // → 'C. civ. art. 1240'
   */
  const CODE_SHORT: Record<string, string> = {
    'code-civil':                   'C. civ.',
    'code-de-commerce':             'C. com.',
    'code-du-travail':              'C. trav.',
    'code-de-procedure-civile':     'C. pr. civ.',
    'code-de-procedure-penale':     'C. pr. pén.',
    'code-penal':                   'C. pén.',
    'code-monetaire-et-financier':  'C. mon. fin.',
    'code-de-la-securite-sociale':  'CSS',
    'code-de-la-propriete-intellectuelle': 'CPI',
    'code-de-justice-administrative': 'CJA',
    'code-general-des-impots-annexe-i-cgiani':   'CGI ann. I',
    'code-general-des-impots-annexe-ii-cgianii':  'CGI ann. II',
    'code-general-des-impots-annexe-iii-cgianiii':'CGI ann. III',
    'code-general-des-impots-annexe-iv-cgianiv':  'CGI ann. IV',
    'reglement-ue-2016-679':                      'RGPD',
    'loi-informatique-et-libertes':               'LIL',
    'livre-des-procedures-fiscales':              'LPF',
    'code-de-lentree-et-du-sejour-des-etrangers-et-du-droit-dasile': 'CESEDA',
    'reglement-ue-2013-604':                      'Dublin III',
  }
  
  /**
   * Nettoie un numéro d'article : supprime le préfixe "Article " ou "art. " stocké
   * dans certaines fiches, pour éviter "… art. Article 2".
   */
  export function cleanArticleNumero(numero: string): string {
    return numero.trim().replace(/^(article|art\.)\s+/i, '')
  }

  export function articleLabel(code: string, numero: string): string {
    const slug  = slugifyCode(code)
    const short = slug ? (CODE_SHORT[slug] ?? null) : null
    const codeLabel = short ?? (slug
      ? slug.replace('code-', 'C. ').replace(/-/g, ' ')
      : code)
    return `${codeLabel} art. ${cleanArticleNumero(numero)}`
  }